import { Switch, Route, useLocation, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { ThemeProvider } from "@/components/theme-provider";
import { ThemeToggle } from "@/components/theme-toggle";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { ProtectedRoute } from "@/components/protected-route";
import { LanguageSwitcher } from "@/components/language-switcher";
import { NotificationCenter } from "@/components/notification-center";
import { MobileBottomNav } from "@/components/mobile-bottom-nav";
import { DemoBanner, isDemoMode } from "@/components/demo-banner";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import "@/i18n/config";
import NotFound from "@/pages/not-found";
import AuthPage from "@/pages/auth-page";
import LandingPage from "@/pages/landing";
import Audit from "@/pages/audit";
import Reports from "@/pages/reports";
import Analytics from "@/pages/analytics";
import Settings from "@/pages/settings";
import Admin from "@/pages/admin";
import WalletPage from "@/pages/wallet";
import WalletHubPage from "@/pages/wallet-hub";
import CreateWalletPage from "@/pages/create-wallet";
import WalletContributePage from "@/pages/wallet-contribute";
import WalletDashboard from "@/pages/wallet-dashboard";
import WalletDiscoverPage from "@/pages/wallet-discover";
import WalletInvite from "@/pages/wallet-invite";
import CommunityFundsPage, { CommunityFundSuccessPage } from "@/pages/community-funds";
import PaymentPlansPage from "@/pages/payment-plans";
import HospitalPartnersPage from "@/pages/hospital-partners";
import HospitalPartnersPublicPage from "@/pages/HospitalPartners";
import SubscriptionsPage from "@/pages/subscriptions";
import ReferralsPage from "@/pages/referrals";
import CommunityPage from "@/pages/community";
import EngagementPage from "@/pages/engagement";
import FinancialTransparencyPage from "@/pages/financial-transparency";
import AdminControlPage from "@/pages/admin-control";
import AdminConditionWalletsPage from "@/pages/admin-condition-wallets";
import AdminCommunityFundsPage from "@/pages/admin-community-funds";
import AdminErrorsPage from "@/pages/admin-errors";
import AmbassadorPage from "@/pages/ambassador";
import PartnerPortalPage from "@/pages/partner-portal";
import PartnerPortalSelfService from "@/pages/PartnerPortal";
import EmployerDashboardPage from "@/pages/employer-dashboard";
import ConditionExplorerPage, { ConditionWalletSuccessPage } from "@/pages/condition-explorer";
import OnboardingPage from "@/pages/onboarding";
import ProfilePage from "@/pages/profile";
import EmergencyFundraisingPage from "@/pages/emergency-fundraising";
import EmergencyLandingPage from "@/pages/emergency-landing";
import SmallBusinessSignupPage from "@/pages/small-business-signup";
import SmallBusinessDashboardPage from "@/pages/small-business-dashboard";
import WalletInvitePage from "@/pages/wallet-invite";
import TermsPage from "@/pages/terms";
import PrivacyPage from "@/pages/privacy";
import CompliancePage from "@/pages/compliance";
import PitchDeckPage from "@/pages/pitch-deck";
import PilotApplyPage from "@/pages/pilot-apply";
import AboutPage from "@/pages/about";
import { MarketingLayout } from "@/components/marketing-layout";
import { ALLOWED_ROLES } from "@/lib/role-routes";

function RedirectTo({ to }: { to: string }) {
  const [, setLocation] = useLocation();
  useEffect(() => {
    setLocation(to);
  }, [to, setLocation]);
  return null;
}

function Router() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <Switch>
        <Route path="/" component={LandingPage} />
        <Route path="/auth" component={AuthPage} />
        <Route path="/wallet/contribute/:token" component={WalletContributePage} />
        <Route path="/wallet/contribute/:token/success" component={WalletContributePage} />
        <Route path="/community-funds">
          <MarketingLayout>
            <CommunityFundsPage />
          </MarketingLayout>
        </Route>
        <Route path="/community-funds/:id/success" component={CommunityFundSuccessPage} />
        <Route path="/conditions">
          <MarketingLayout>
            <ConditionExplorerPage />
          </MarketingLayout>
        </Route>
        <Route path="/conditions/:conditionId">
          <MarketingLayout>
            <ConditionExplorerPage />
          </MarketingLayout>
        </Route>
        <Route path="/condition-wallets/:walletId/contribute/success" component={ConditionWalletSuccessPage} />
        <Route path="/condition-wallets/:walletId" component={ConditionExplorerPage} />
        <Route path="/wallet/discover">
          <MarketingLayout>
            <WalletDiscoverPage />
          </MarketingLayout>
        </Route>
        <Route path="/emergency-fundraising" component={EmergencyLandingPage} />
        <Route path="/emergency/:shareToken" component={EmergencyFundraisingPage} />
        <Route path="/wallet-invite/:token" component={WalletInvitePage} />
        <Route path="/small-business/signup">
          <MarketingLayout>
            <SmallBusinessSignupPage />
          </MarketingLayout>
        </Route>
        <Route path="/terms" component={TermsPage} />
        <Route path="/privacy" component={PrivacyPage} />
        <Route path="/compliance" component={CompliancePage} />
        <Route path="/pitch" component={PitchDeckPage} />
        <Route path="/pilot-apply" component={PilotApplyPage} />
        <Route path="/about" component={AboutPage} />
        <Route path="/hospital-partners" component={HospitalPartnersPublicPage} />
        <Route>
          <Redirect to="/auth" />
        </Route>
      </Switch>
    );
  }

  if (user.role === "supporter" && !user.onboardingCompleted) {
    return (
      <Switch>
        <Route path="/onboarding" component={OnboardingPage} />
        <Route>
          <RedirectTo to="/onboarding" />
        </Route>
      </Switch>
    );
  }

  return (
    <Switch>
      <Route path="/auth" component={AuthPage} />
      <Route path="/onboarding">
        <RedirectTo to="/" />
      </Route>
      <Route path="/profile">
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      </Route>
      <Route path="/home">
        <RedirectTo to="/wallet" />
      </Route>
      <Route path="/pitch" component={PitchDeckPage} />
      <Route path="/pilot-apply" component={PilotApplyPage} />
      <Route path="/about" component={AboutPage} />
      <Route path="/emergency-fundraising" component={EmergencyLandingPage} />
      <Route path="/emergency/:shareToken" component={EmergencyFundraisingPage} />
      <Route path="/wallet-invite/:token" component={WalletInvitePage} />
      <Route path="/small-business/signup" component={SmallBusinessSignupPage} />
      <Route path="/small-business/dashboard">
        <ProtectedRoute allowedRoles={ALLOWED_ROLES.employer} redirectTo="/auth">
          <SmallBusinessDashboardPage />
        </ProtectedRoute>
      </Route>
      <Route path="/">
        <ProtectedRoute autoRedirectHospitalPartners={true} autoRedirectEmployerAdmins={true}>
          <WalletHubPage />
        </ProtectedRoute>
      </Route>
      <Route path="/audit">
        <ProtectedRoute allowedRoles={ALLOWED_ROLES.audit}>
          <Audit />
        </ProtectedRoute>
      </Route>
      <Route path="/reports">
        <ProtectedRoute allowedRoles={ALLOWED_ROLES.reports}>
          <Reports />
        </ProtectedRoute>
      </Route>
      <Route path="/analytics">
        <ProtectedRoute allowedRoles={ALLOWED_ROLES.analytics}>
          <Analytics />
        </ProtectedRoute>
      </Route>
      <Route path="/admin/hospital-partners">
        <ProtectedRoute allowedRoles={ALLOWED_ROLES.adminHospitalPartners}>
          <HospitalPartnersPage />
        </ProtectedRoute>
      </Route>
      <Route path="/hospital-partners" component={HospitalPartnersPublicPage} />
      <Route path="/partner-portal">
        <ProtectedRoute allowedRoles={ALLOWED_ROLES.partnerPortal}>
          <PartnerPortalSelfService />
        </ProtectedRoute>
      </Route>
      <Route path="/partner-portal/:id">
        <ProtectedRoute allowedRoles={ALLOWED_ROLES.partnerPortal}>
          <PartnerPortalPage />
        </ProtectedRoute>
      </Route>
      <Route path="/admin">
        <ProtectedRoute allowedRoles={ALLOWED_ROLES.superAdmin}>
          <Admin />
        </ProtectedRoute>
      </Route>
      {/* /admin-control is accessible to both admin and dispatcher.
          ALLOWED_ROLES.adminControl and getPostLoginRedirect() in role-routes.ts
          are the single source of truth — both roles are declared there together. */}
      <Route path="/admin-control">
        <ProtectedRoute allowedRoles={ALLOWED_ROLES.adminControl}>
          <AdminControlPage />
        </ProtectedRoute>
      </Route>
      <Route path="/admin/condition-wallets">
        <ProtectedRoute allowedRoles={ALLOWED_ROLES.adminConditionWallets}>
          <AdminConditionWalletsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/admin-condition-wallets">
        <ProtectedRoute allowedRoles={ALLOWED_ROLES.adminConditionWallets}>
          <AdminConditionWalletsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/admin/community-funds">
        <ProtectedRoute allowedRoles={ALLOWED_ROLES.adminConditionWallets}>
          <AdminCommunityFundsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/admin/errors">
        <ProtectedRoute allowedRoles={ALLOWED_ROLES.adminErrors}>
          <AdminErrorsPage />
        </ProtectedRoute>
      </Route>
      <Route path="/settings">
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      </Route>
      <Route path="/payment-plans">
        <ProtectedRoute>
          <PaymentPlansPage />
        </ProtectedRoute>
      </Route>
      <Route path="/wallet/create">
        <ProtectedRoute>
          <CreateWalletPage />
        </ProtectedRoute>
      </Route>
      <Route path="/wallet/dashboard">
        <ProtectedRoute>
          <WalletDashboard />
        </ProtectedRoute>
      </Route>
      <Route path="/wallet-dashboard">
        <RedirectTo to="/wallet/dashboard" />
      </Route>
      <Route path="/wallet/discover" component={WalletDiscoverPage} />
      <Route path="/wallet/contribute/:token/success" component={WalletContributePage} />
      <Route path="/wallet/contribute/:token" component={WalletContributePage} />
      <Route path="/wallet-invite/:token" component={WalletInvite} />
      <Route path="/wallet/:walletId">
        <ProtectedRoute>
          <WalletPage />
        </ProtectedRoute>
      </Route>
      <Route path="/wallet">
        <ProtectedRoute>
          <WalletHubPage />
        </ProtectedRoute>
      </Route>
      <Route path="/community-funds" component={CommunityFundsPage} />
      <Route path="/community-funds/:id/success" component={CommunityFundSuccessPage} />
      <Route path="/subscriptions" component={SubscriptionsPage} />
      <Route path="/referrals" component={ReferralsPage} />
      <Route path="/community" component={CommunityPage} />
      <Route path="/engagement" component={EngagementPage} />
      <Route path="/financial-transparency" component={FinancialTransparencyPage} />
      <Route path="/employer">
        <ProtectedRoute allowedRoles={ALLOWED_ROLES.employer} redirectTo="/auth">
          <EmployerDashboardPage />
        </ProtectedRoute>
      </Route>
      <Route path="/ambassador" component={AmbassadorPage} />
      <Route path="/conditions" component={ConditionExplorerPage} />
      <Route path="/conditions/:conditionId" component={ConditionExplorerPage} />
      <Route path="/condition-wallets/:walletId/contribute/success" component={ConditionWalletSuccessPage} />
      <Route path="/condition-wallets/:walletId" component={ConditionExplorerPage} />
      <Route path="/terms" component={TermsPage} />
      <Route path="/privacy" component={PrivacyPage} />
      <Route path="/compliance" component={CompliancePage} />
      <Route path="/create-wallet">
        <Redirect to="/wallet/create" />
      </Route>
      <Route path="/employer-dashboard">
        <Redirect to="/employer" />
      </Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  const style = {
    "--sidebar-width": "16rem",
    "--sidebar-width-icon": "3rem",
  };

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <DemoBanner />
          <AuthProvider>
            <AuthenticatedLayout style={style as React.CSSProperties}>
              <Router />
            </AuthenticatedLayout>
          </AuthProvider>
          <Toaster />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

function AuthenticatedLayout({ children, style }: { children: React.ReactNode; style: React.CSSProperties }) {
  const { user, isLoading } = useAuth();
  const { i18n } = useTranslation();
  const [location] = useLocation();

  useEffect(() => {
    if (user?.language) {
      i18n.changeLanguage(user.language);
      document.documentElement.lang = user.language;
      localStorage.setItem('healthwallet-language', user.language);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.language]);

  if (isLoading || !user || location.startsWith("/pitch") || location.startsWith("/pilot-apply") || location === "/about") {
    return isDemoMode ? <div className="pt-8">{children}</div> : <>{children}</>;
  }

  return (
    <>
      {isDemoMode && <div className="h-8" aria-hidden="true" />}
      <SidebarProvider style={style}>
      <div className={`flex w-full ${isDemoMode ? 'h-[calc(100vh-2rem)]' : 'h-screen'}`}>
        <AppSidebar />
        <div className="flex flex-col flex-1 min-w-0">
          <header className="flex items-center justify-between h-14 sm:h-16 px-3 sm:px-6 gap-2 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
            <SidebarTrigger data-testid="button-sidebar-toggle" className="min-h-[44px] w-11" />
            <div className="flex items-center gap-1.5 sm:gap-2">
              <NotificationCenter />
              <LanguageSwitcher />
              <ThemeToggle />
            </div>
          </header>
          <main className="flex-1 overflow-auto">
            <div className="container mx-auto px-3 py-4 sm:p-6 max-w-screen-2xl pb-20 md:pb-6">
              {children}
            </div>
          </main>
          <MobileBottomNav />
        </div>
      </div>
    </SidebarProvider>
    </>
  );
}

export default App;
